import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from '../team-report/KeyTakeaway';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { SessionDurationRow, ReportSection, formatDuration } from './types';

interface Props {
    data: ReportSection<SessionDurationRow>;
}

const SessionDurationSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const chartData = data.rows.map((r) => ({
        session: `S${r.session_number}`,
        avg: Math.round(r.avg_seconds / 60 * 10) / 10,
        median: Math.round(r.median_seconds / 60 * 10) / 10,
        avgRaw: r.avg_seconds,
        medianRaw: r.median_seconds,
    }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-session-duration">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">1. Session Duration Overview</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Average duration per session across all athletes. Only fully completed sessions are included.
            </Typography>

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={chartData} margin={{ bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="session" tick={{ fontSize: 12 }} />
                        <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                        <Tooltip
                            formatter={(val: number, name: string) => [`${val} min`, name]}
                        />
                        <Bar dataKey="avg" name="Average" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="median" name="Median" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                        <Legend />
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Session</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">Median</TableCell>
                                <TableCell align="right">Min</TableCell>
                                <TableCell align="right">Max</TableCell>
                                <TableCell align="right">Completed</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.session_number}>
                                    <TableCell>Session {r.session_number}</TableCell>
                                    <TableCell align="right">{formatDuration(r.avg_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.median_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.min_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.max_seconds)}</TableCell>
                                    <TableCell align="right">{r.completion_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default SessionDurationSection;
